import React, { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { occupationApi } from '@/services/skillKnowledgeApi';
import { usePaginatedList, useMutation } from '@/hooks';
import { Button } from '@/components/ui/Button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Plus, Eye, Edit, Trash2, Briefcase } from 'lucide-react';
import { LoadingState, EmptyState } from '@/components/common';
import { formatDate } from '@/utils/dateFormatter';
import type { Occupation } from '@/types/skillKnowledge';

export const OccupationListPage: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAdminAuth();

  const canCreate = hasPermission('create', 'occupation');
  const canEdit = hasPermission('edit', 'occupation');
  const canDelete = hasPermission('delete', 'occupation');

  // Build fetch function
  const fetchOccupationsFn = useMemo(() => {
    return async (params: { page: number; size: number }) => {
      return occupationApi.list({
        page: params.page,
        size: params.size,
        sortBy: 'title',
        sortDirection: 'asc',
      });
    };
  }, []);

  // Use paginated list hook
  const {
    data: occupations,
    isLoading,
    error,
    pagination,
    setPage,
    setSize: _setSize,
    refetch,
  } = usePaginatedList<Occupation>({
    fetchFn: fetchOccupationsFn,
    entityName: 'occupations',
    suppress404Errors: true,
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await occupationApi.delete(id);
    },
    entityName: 'occupation',
    operation: 'delete',
    onSuccess: () => {
      refetch();
    },
  });

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this occupation?')) return;
    await deleteMutation.mutate(id);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Occupation Management</h1>
          <p className="text-muted-foreground mt-2">Manage all occupations</p>
        </div>
        {canCreate && (
          <Button asChild>
            <Link to="/admin/occupations/new">
              <Plus className="mr-2 h-4 w-4" />
              Create Occupation
            </Link>
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Occupations ({pagination.totalElements})</CardTitle>
          <CardDescription>List of all occupations</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <LoadingState message="Loading occupations..." />
          ) : (error || occupations.length === 0) ? (
            <EmptyState
              title="No data found"
              description={error ? "Unable to load occupations. Please try again or create a new one." : "Get started by creating your first occupation."}
              icon={Briefcase}
              actions={[
                ...(canCreate ? [{
                  label: 'Create a new one',
                  onClick: () => navigate('/admin/occupations/new'),
                  variant: 'default' as const,
                }] : []),
                {
                  label: 'Try again',
                  onClick: () => refetch(),
                  variant: 'outline' as const,
                },
              ]}
            />
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Industry</TableHead>
                    <TableHead>Career Level</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created At</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {occupations.map((occupation) => (
                    <TableRow key={occupation.id}>
                      <TableCell className="font-medium">{occupation.title}</TableCell>
                      <TableCell>{occupation.industry || 'N/A'}</TableCell>
                      <TableCell>{occupation.careerLevel}</TableCell>
                      <TableCell>{occupation.occupationType}</TableCell>
                      <TableCell>
                        <span
                          className={`px-2 py-1 rounded-full text-xs ${
                            occupation.status === 'ACTIVE'
                              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                              : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
                          }`}
                        >
                          {occupation.status}
                        </span>
                      </TableCell>
                      <TableCell>
                        {formatDate(occupation.createdAt)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="icon" asChild>
                            <Link to={`/admin/occupations/${occupation.id}`}>
                              <Eye className="h-4 w-4" />
                            </Link>
                          </Button>
                          {canEdit && (
                            <Button variant="ghost" size="icon" asChild>
                              <Link to={`/admin/occupations/${occupation.id}/edit`}>
                                <Edit className="h-4 w-4" />
                              </Link>
                            </Button>
                          )}
                          {canDelete && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => occupation.id && handleDelete(occupation.id)}
                              disabled={deleteMutation.isLoading}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {(pagination.totalPages > 1 || pagination.totalElements > pagination.size) && (
                <div className="flex items-center justify-between mt-4">
                  <div className="text-sm text-muted-foreground">
                    Page {pagination.page + 1} of {pagination.totalPages || 1} ({pagination.totalElements} total)
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(Math.max(0, pagination.page - 1))}
                      disabled={pagination.page === 0 || isLoading}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(Math.min((pagination.totalPages || 1) - 1, pagination.page + 1))}
                      disabled={pagination.page >= (pagination.totalPages || 1) - 1 || isLoading}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default OccupationListPage;
